import type { ReactNode } from 'react';
import { Flex, FormattedDateTime, Icon, Text, Tooltip, TooltipContent } from '@customerio/pluma-components/react';
import type { CopilotAgentUsageMeterBody } from '@customerio/hydra-ui-api';

/** The displayed percent where the reach-date runway copy starts. */
const CAUTION_PERCENT = 75;

/**
 * The displayed percent for the agent usage meter. The label keeps counting past the line
 * (105%, 120%) per the prototype, so it derives from raw credits — used_percent arrives
 * clamped to 0-1 from the endpoint.
 */
export function agentUsedPercent(meter: CopilotAgentUsageMeterBody): number {
  return meter.allowance_credits > 0 ? Math.round((meter.used_credits / meter.allowance_credits) * 100) : 0;
}

/**
 * The runway sentence under the bar: on-pace below the caution line, reach-date to 100%, reached
 * past it. Thresholds compare the same rounded percent the copy shows, so the label and the state
 * can't disagree (0.996 reads "100%" and is treated as reached).
 */
export function agentRunwayLine(meter: CopilotAgentUsageMeterBody, percent: number): ReactNode {
  if (percent >= 100) {
    return <>You&apos;ve reached your included usage for this cycle.</>;
  }
  if (percent >= CAUTION_PERCENT && meter.projected_exhaust_at != null) {
    return (
      <>
        At this pace you&apos;ll reach your included usage around{' '}
        <FormattedDateTime withTooltip={false} date={new Date(meter.projected_exhaust_at).getTime()} format="medium" />.
      </>
    );
  }
  return (
    <>
      You&apos;re on pace to use about {Math.round(meter.projected_percent * 100)}% of your included usage this cycle.
    </>
  );
}

type AgentEnforcementNoteProps = {
  meter: CopilotAgentUsageMeterBody;
  percent: number;
  sizeVariant?: 'md' | 'xs';
};

/** The beta "not enforcing" note, with the no-charge reassurance once the line is crossed. */
export function AgentEnforcementNote({ meter, percent, sizeVariant = 'md' }: AgentEnforcementNoteProps) {
  if (meter.enforcing) {
    return null;
  }
  return (
    <Flex alignItems="center" gap="050">
      <Text text={sizeVariant === 'xs' ? 'product-pxs' : 'product-p'} color="subtle">
        We&apos;re not enforcing limits at this time.
        {/* Past the line the prototype adds the no-charge reassurance in place of any banner. */}
        {percent >= 100 ? <> You won&apos;t be charged for overages.</> : null}
      </Text>
      <Tooltip placement="top-end">
        <Icon name="help" size="sm" color="subtle" />
        <TooltipContent>Usage past the included amount isn&apos;t charged or blocked during the beta.</TooltipContent>
      </Tooltip>
    </Flex>
  );
}
